/**
 * Staged first-run coaching + reopenable "How to fight" sheet.
 * Progress is device-local (localStorage); v1 dismiss flag migrates as "all done".
 */

import { createCardArt } from "../../../shared/cards/cardFace";

export type CoachProgress = {
  intro: boolean;
  firstProclaim: boolean;
  firstAbide: boolean;
  firstWillDrain: boolean;
  firstDefeat: boolean;
  firstSettled: boolean;
};

export type CoachStepId =
  | "intro"
  | "firstProclaim"
  | "firstAbide"
  | "firstWillDrain"
  | "firstSettled"
  | "sheet";

export type IntroContext = {
  /** e.g. "Hold until the timer ends" / "Survive as long as you can" */
  objective: string;
  /** Mission blurb or free-play focus line */
  focus?: string;
  endless?: boolean;
};

const STORAGE_KEY = "sots-coach-v2";
const LEGACY_KEY = "sots-coach-v1-dismissed";

const EMPTY: CoachProgress = {
  intro: false,
  firstProclaim: false,
  firstAbide: false,
  firstWillDrain: false,
  firstDefeat: false,
  firstSettled: false,
};

export function loadCoachProgress(): CoachProgress {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<CoachProgress>;
      return {
        intro: !!p.intro,
        firstProclaim: !!p.firstProclaim,
        // Missing field (older v2) → false so the new Abide tip can still show once
        firstAbide: !!p.firstAbide,
        firstWillDrain: !!p.firstWillDrain,
        firstDefeat: !!p.firstDefeat,
        firstSettled: !!p.firstSettled,
      };
    }
    // Returning players who finished the old one-shot coach skip early tips;
    // firstAbide / firstSettled stay false so those tips can still show once.
    if (localStorage.getItem(LEGACY_KEY) === "1") {
      const done: CoachProgress = {
        intro: true,
        firstProclaim: true,
        firstAbide: false,
        firstWillDrain: true,
        firstDefeat: true,
        firstSettled: false,
      };
      saveCoachProgress(done);
      return done;
    }
  } catch {
    /* ignore */
  }
  return { ...EMPTY };
}

export function saveCoachProgress(p: CoachProgress): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

export function markCoachStep(
  step: keyof CoachProgress,
  progress: CoachProgress = loadCoachProgress(),
): CoachProgress {
  if (progress[step]) return progress;
  const next = { ...progress, [step]: true };
  saveCoachProgress(next);
  return next;
}

/** True if any staged tip or first-defeat line has been marked done. */
export function hasCoachProgress(): boolean {
  try {
    if (localStorage.getItem(STORAGE_KEY)) return true;
    if (localStorage.getItem(LEGACY_KEY) === "1") return true;
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * Clear staged tutorial tips so they show again on the next fights.
 * Does not touch campaign, collection, or decks.
 */
export function resetCoachProgress(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* ignore */
  }
}

/** Short status for Settings. */
export function coachProgressSummary(): {
  seen: boolean;
  stepsDone: number;
  stepsTotal: number;
} {
  const p = loadCoachProgress();
  const flags: (keyof CoachProgress)[] = [
    "intro",
    "firstProclaim",
    "firstAbide",
    "firstWillDrain",
    "firstDefeat",
    "firstSettled",
  ];
  const stepsDone = flags.filter((k) => p[k]).length;
  return {
    seen: stepsDone > 0 || hasCoachProgress(),
    stepsDone,
    stepsTotal: flags.length,
  };
}

type StepCopy = {
  title: string;
  lead?: string;
  list: string[];
  note?: string;
  cta: string;
};

function sheetCopy(): StepCopy {
  return {
    title: "How to fight",
    lead: "Speak the Word. Lies fall silent.",
    list: [
      "<strong>Goal</strong> — Survive the stage (timer) without integrity hitting 0. Endless: hold as long as you can.",
      "<strong>Board</strong> — Mind → Heart → Will → Secret Place. Lies press inward.",
      "<strong>Play</strong> — Drag a verse onto a lit lie or slot. Valid targets light up while you drag.",
      "<strong>Abide</strong> — Some verses are <strong>Prayers</strong>: drag onto an <strong>empty slot</strong> (or layer). They stay and press toward Truth. Don’t drop them on a lie — that is for Answer.",
      "<strong>Cost</strong> — Speaking locks further plays for a few seconds. Longer verses cost more time.",
      "<strong>Threat</strong> — A lie on <strong>Will</strong> drains integrity. Clear Will first.",
      "<strong>Queue</strong> — While speaking, drop another card to line it up.",
      "<strong>Study</strong> — Tap a card’s art to read the verse and abilities (optional under fire).",
    ],
    note: "Space opens the pause menu. Match types (Faith vs Fear, etc.) hit harder — study cards to learn Meets.",
    cta: "Close",
  };
}

function introCopy(ctx: IntroContext): StepCopy {
  const list = [
    "<strong>Drag</strong> a verse onto a highlighted lie or slot.",
    "<strong>Speech locks you</strong> while you proclaim — that wait is the cost.",
    "A lie on <strong>Will</strong> drains integrity. Keep the Will clear.",
  ];
  if (ctx.endless) {
    list.push("Score is how long you hold. Integrity 0 ends the run.");
  } else {
    list.push("Hold until the timer ends — you do not need to silence every lie.");
  }
  return {
    title: "How to fight",
    lead: "Lies press Mind → Heart → Will. You answer by speaking Scripture.",
    list,
    note: "Valid targets light up when you drag (mouse or finger). Space pauses anytime.",
    cta: "Got it — fight",
  };
}

export type CoachExampleCard = {
  verseId: string;
  reference: string;
  category: string;
  speakSeconds?: number;
};

export type CoachExampleSet = {
  label: string;
  cards: CoachExampleCard[];
};

export type CoachPeelStep = {
  category: string;
  label: string;
  current?: boolean;
  /** In this fight's deck */
  inDeck: boolean;
  /** Owned but not in the loadout */
  owned: boolean;
  hint: string;
};

function firstProclaimCopy(answers?: CoachExampleCard[]): StepCopy {
  const n = answers?.length ?? 0;
  return {
    title: "You’re speaking",
    lead: "The verse is resolving — the board still moves while your mouth is busy.",
    list: [
      "You cannot play another card until the proclaim bar finishes.",
      "Drop a second card while speaking to <strong>queue</strong> it (plays when speech ends).",
      n > 0
        ? `<strong>Answer</strong> cards (shown below) weaken lies — drag them onto a lit lie, not an empty slot.`
        : "Short snipes chain faster; long verses buy power at the cost of time.",
      n > 0
        ? "Short snipes chain faster; longer Answers cost more speak time."
        : "Hover a card for drag hints while you aim.",
    ],
    note: "Queue shows above the proclaim strip — Cancel if you change your mind.",
    cta: "Continue",
  };
}

function firstAbideCopy(prayers?: CoachExampleCard[]): StepCopy {
  const n = prayers?.length ?? 0;
  const names =
    n > 0
      ? prayers!
          .slice(0, 3)
          .map((c) => escapeText(c.reference))
          .join(", ") + (n > 3 ? ` · +${n - 3} more` : "")
      : "";
  return {
    title: "Plant a Prayer",
    lead:
      n > 1
        ? `These Abide verses stay on the board as Prayers — not one-shot Answers.`
        : n === 1
          ? `${prayers![0]!.reference} is an Abide verse — it stays on the board as a Prayer, not a one-shot Answer.`
          : "Some verses Abide — they stay on the board as Prayers, not one-shot Answers.",
    list: [
      n > 0
        ? `Drag a Prayer (${names}) onto an <strong>empty slot</strong> (Mind, Heart, or Will).`
        : "Drag a Prayer verse onto an <strong>empty slot</strong> (open space on Mind, Heart, or Will).",
      "<strong>Do not</strong> drop it on a lie — that is for Answer cards (shown after your first play).",
      "Empty slots light up while you drag. Will is always open; Mind and Heart may need a foothold.",
      "Prayers press toward Truth and can be worn down by lies — Restore mends them.",
    ],
    note:
      n > 0
        ? "Cards above are in this fight’s deck. Hover one in hand for “plant a Prayer,” then drop on a lit empty slot."
        : "Hover a Prayer card for drag hints — empty slots light gold.",
    cta: "Plant a Prayer",
  };
}

function firstWillDrainCopy(): StepCopy {
  return {
    title: "Will under pressure",
    lead: "A lie is holding the Will — that is why integrity is falling.",
    list: [
      "Prioritize lies on <strong>Will</strong> (bottom layer, above the Secret Place).",
      "Answer or Soften them, or place a Prayer on Will to hold the line.",
      "Mind and Heart still matter, but Will drain ends the fight.",
    ],
    note: "Open Pause → How to fight anytime for the full cheat sheet.",
    cta: "Clear the Will",
  };
}

export class CoachUi {
  private overlay: HTMLElement;
  private titleEl: HTMLElement;
  private leadEl: HTMLElement;
  private objectiveEl: HTMLElement;
  private exampleEl: HTMLElement | null;
  private exampleLabelEl: HTMLElement | null;
  private exampleCardEl: HTMLElement | null;
  private peelEl: HTMLElement | null;
  private listEl: HTMLOListElement;
  private noteEl: HTMLElement;
  private ctaBtn: HTMLButtonElement;
  private step: CoachStepId | null = null;
  /** True when this coach instance paused the game for teaching. */
  private wePaused = false;
  private onDismiss: ((step: CoachStepId, wePaused: boolean) => void) | null =
    null;

  constructor(overlay: HTMLElement) {
    this.overlay = overlay;
    this.titleEl = overlay.querySelector("#coach-title")!;
    this.leadEl = overlay.querySelector("#coach-lead")!;
    this.objectiveEl = overlay.querySelector("#coach-objective")!;
    this.exampleEl = overlay.querySelector("#coach-example");
    this.exampleLabelEl = overlay.querySelector("#coach-example-label");
    this.exampleCardEl = overlay.querySelector("#coach-example-card");
    this.peelEl = overlay.querySelector("#coach-peel");
    this.listEl = overlay.querySelector("#coach-list")!;
    this.noteEl = overlay.querySelector("#coach-note")!;
    this.ctaBtn = overlay.querySelector("#btn-coach-dismiss")!;

    this.ctaBtn.addEventListener("click", () => this.dismiss());
  }

  isOpen(): boolean {
    return !this.overlay.classList.contains("hidden");
  }

  currentStep(): CoachStepId | null {
    return this.step;
  }

  didPauseForCoach(): boolean {
    return this.wePaused;
  }

  setOnDismiss(
    fn: ((step: CoachStepId, wePaused: boolean) => void) | null,
  ): void {
    this.onDismiss = fn;
  }

  showIntro(ctx: IntroContext, opts?: { pause: boolean }): void {
    const copy = introCopy(ctx);
    this.fill(copy);
    this.clearExample();
    this.renderObjective(ctx);
    this.open("intro", opts?.pause ?? true);
  }

  showFirstProclaim(
    opts?: { pause: boolean; answers?: CoachExampleCard[] },
  ): void {
    const answers = opts?.answers ?? [];
    this.fill(firstProclaimCopy(answers));
    this.clearObjective();
    this.renderExamples({
      label:
        answers.length > 1
          ? `Answer cards in this fight (${answers.length})`
          : answers.length === 1
            ? "Answer card in this fight"
            : "Answer cards",
      cards: answers,
    });
    this.open("firstProclaim", opts?.pause ?? true);
  }

  showFirstAbide(
    opts?: { pause: boolean; prayers?: CoachExampleCard[] },
  ): void {
    const prayers = opts?.prayers ?? [];
    this.fill(firstAbideCopy(prayers));
    this.clearObjective();
    this.renderExamples({
      label:
        prayers.length > 1
          ? `Abide / Prayer cards in this fight (${prayers.length})`
          : prayers.length === 1
            ? "Abide / Prayer in this fight"
            : "Abide / Prayer",
      cards: prayers,
    });
    this.open("firstAbide", opts?.pause ?? true);
  }

  showFirstWillDrain(opts?: { pause: boolean }): void {
    this.fill(firstWillDrainCopy());
    this.clearExample();
    this.clearObjective();
    this.open("firstWillDrain", opts?.pause ?? true);
  }

  /** One-shot Settled tip (does not mark tutorial progress). */
  showDogmaNotice(
    copy: {
      title: string;
      lead: string;
      list: string[];
      note?: string;
    },
    opts?: {
      pause?: boolean;
      cards?: CoachExampleCard[];
      peel?: CoachPeelStep[];
    },
  ): void {
    this.fill({
      title: copy.title,
      lead: copy.lead,
      list: copy.list,
      note: copy.note ?? "",
      cta: "Understood",
    });
    this.clearObjective();
    if (opts?.cards?.length) {
      this.renderExamples({
        label: "In this fight",
        cards: opts.cards,
      });
    } else {
      this.clearExample();
    }
    this.renderPeelLadder(opts?.peel);
    this.open("firstSettled", opts?.pause ?? true);
  }

  /** Reopenable full sheet (does not mark tutorial progress). */
  showSheet(opts?: { pause: boolean }): void {
    this.fill(sheetCopy());
    this.clearExample();
    this.clearObjective();
    this.open("sheet", opts?.pause ?? false);
  }

  hide(): void {
    this.overlay.classList.add("hidden");
    this.clearExample();
    this.clearPeelLadder();
    this.step = null;
    this.wePaused = false;
  }

  dismiss(): void {
    const step = this.step;
    const wePaused = this.wePaused;
    if (step && step !== "sheet") {
      markCoachStep(step);
    }
    this.hide();
    if (step) this.onDismiss?.(step, wePaused);
  }

  private open(step: CoachStepId, pause: boolean): void {
    this.step = step;
    this.wePaused = pause;
    this.overlay.classList.remove("hidden");
  }

  private fill(copy: StepCopy): void {
    this.titleEl.textContent = copy.title;
    if (copy.lead) {
      this.leadEl.hidden = false;
      this.leadEl.textContent = copy.lead;
    } else {
      this.leadEl.hidden = true;
      this.leadEl.textContent = "";
    }
    this.listEl.replaceChildren();
    for (const item of copy.list) {
      const li = document.createElement("li");
      li.innerHTML = item;
      this.listEl.appendChild(li);
    }
    if (copy.note) {
      this.noteEl.hidden = false;
      this.noteEl.textContent = copy.note;
    } else {
      this.noteEl.hidden = true;
      this.noteEl.textContent = "";
    }
    this.ctaBtn.textContent = copy.cta;
  }

  private renderObjective(ctx: IntroContext): void {
    this.objectiveEl.hidden = false;
    this.objectiveEl.replaceChildren();
    const win = document.createElement("p");
    win.className = "coach-objective-win";
    win.innerHTML = `<strong>Win</strong> — ${escapeText(ctx.objective)}`;
    this.objectiveEl.appendChild(win);
    if (ctx.focus) {
      const focus = document.createElement("p");
      focus.className = "coach-objective-focus";
      focus.innerHTML = `<strong>Focus</strong> — ${escapeText(ctx.focus)}`;
      this.objectiveEl.appendChild(focus);
    }
  }

  private clearObjective(): void {
    this.objectiveEl.hidden = true;
    this.objectiveEl.replaceChildren();
  }

  private renderExamples(set: CoachExampleSet | null | undefined): void {
    if (!this.exampleEl || !this.exampleCardEl) return;
    if (!set?.cards.length) {
      this.clearExample();
      return;
    }
    this.exampleEl.hidden = false;
    this.exampleEl.classList.remove("hidden");
    if (this.exampleLabelEl) {
      this.exampleLabelEl.textContent = set.label;
    }
    this.exampleCardEl.replaceChildren();
    this.exampleCardEl.classList.toggle("is-many", set.cards.length > 1);

    for (const example of set.cards) {
      const mini = document.createElement("article");
      mini.className = `card category-${example.category} clean-face coach-mini-card`;
      mini.setAttribute("aria-label", example.reference);
      mini.title = example.reference;

      const speak =
        example.speakSeconds !== undefined
          ? Math.round(example.speakSeconds * 10) / 10
          : undefined;
      const art = createCardArt({
        verseId: example.verseId,
        category: example.category,
        reference: example.reference,
        speakSeconds: speak,
        compactRef: true,
      });
      mini.append(art);
      this.exampleCardEl.appendChild(mini);
    }
  }

  private renderPeelLadder(steps?: CoachPeelStep[]): void {
    const root = this.peelEl;
    if (!root) return;
    if (!steps?.length) {
      this.clearPeelLadder();
      return;
    }
    root.hidden = false;
    root.classList.remove("hidden");
    root.replaceChildren();
    const lead = document.createElement("p");
    lead.className = "coach-peel-lead";
    lead.textContent =
      "Each shell needs that color’s Answer. Wrong color only chips it as a III.";
    root.appendChild(lead);
    const row = document.createElement("ol");
    row.className = "coach-peel-row";
    for (const step of steps) {
      const li = document.createElement("li");
      li.className =
        `coach-peel-step cat-${step.category}` +
        (step.current ? " is-current" : "") +
        (step.inDeck ? " is-ready" : " is-missing");
      const pip = document.createElement("span");
      pip.className = "coach-peel-pip";
      const name = document.createElement("strong");
      name.textContent = step.label;
      const status = document.createElement("span");
      status.className = "coach-peel-status";
      status.textContent = step.inDeck
        ? "In this deck"
        : step.owned
          ? "Owned — add in Deck"
          : "Add in Deck builder";
      const hint = document.createElement("span");
      hint.className = "coach-peel-hint";
      hint.textContent = step.hint;
      li.append(pip, name, status, hint);
      row.appendChild(li);
    }
    root.appendChild(row);
  }

  private clearPeelLadder(): void {
    if (!this.peelEl) return;
    this.peelEl.hidden = true;
    this.peelEl.classList.add("hidden");
    this.peelEl.replaceChildren();
  }

  private clearExample(): void {
    if (this.exampleEl) {
      this.exampleEl.hidden = true;
      this.exampleEl.classList.add("hidden");
    }
    if (this.exampleLabelEl) {
      this.exampleLabelEl.textContent = "In this fight";
    }
    this.exampleCardEl?.classList.remove("is-many");
    this.exampleCardEl?.replaceChildren();
  }
}

function escapeText(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Short objective strip under the top bar (non-modal). */
export class ObjectiveBanner {
  private root: HTMLElement;
  private winEl: HTMLElement;
  private focusEl: HTMLElement;
  private hideTimer = 0;

  constructor(root: HTMLElement) {
    this.root = root;
    this.winEl = root.querySelector("#objective-win")!;
    this.focusEl = root.querySelector("#objective-focus")!;
    root.querySelector("#objective-dismiss")?.addEventListener("click", () => {
      this.hide();
    });
  }

  show(opts: { objective: string; focus?: string; autoHideMs?: number }): void {
    this.winEl.textContent = opts.objective;
    if (opts.focus) {
      this.focusEl.hidden = false;
      this.focusEl.textContent = opts.focus;
    } else {
      this.focusEl.hidden = true;
      this.focusEl.textContent = "";
    }
    this.root.classList.remove("hidden");
    window.clearTimeout(this.hideTimer);
    const ms = opts.autoHideMs ?? 9000;
    if (ms > 0) {
      this.hideTimer = window.setTimeout(() => this.hide(), ms);
    }
  }

  hide(): void {
    window.clearTimeout(this.hideTimer);
    this.hideTimer = 0;
    this.root.classList.add("hidden");
  }

  isOpen(): boolean {
    return !this.root.classList.contains("hidden");
  }
}
